import React, { useState, useEffect } from 'react';
import { X, Calendar, RefreshCw, XCircle, ChevronLeft, CreditCard, CheckCircle } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const AppointmentModal = ({ isOpen, onClose, prefilledUHID = '', prefilledAppointmentId = '', initialAction = '', onSuccess }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // New Appointment
    uhid: '',
    hospital: '',
    speciality: '',
    doctor: '',
    appointmentDate: '',
    slot: '',
    // Reschedule
    rescheduleId: '',
    newHospital: '',
    newSpeciality: '',
    newDoctor: '',
    newDate: '',
    newSlot: '',
    // Cancel
    cancelId: '',
    cancelReason: '',
    cancelComments: ''
  });

  // Handle prefilled UHID
  useEffect(() => {
    if (isOpen && prefilledUHID) {
      setFormData(prev => ({ ...prev, uhid: prefilledUHID }));
    }
  }, [isOpen, prefilledUHID]);

  // Handle prefilled Appointment ID and auto-select action
  useEffect(() => {
    if (isOpen && prefilledAppointmentId) {
      setFormData(prev => ({ ...prev, rescheduleId: prefilledAppointmentId, cancelId: prefilledAppointmentId }));
      if (initialAction === 'reschedule') {
        setSelectedOption('reschedule');
      } else if (initialAction === 'cancel') {
        setSelectedOption('cancel');
      }
    }
  }, [isOpen, prefilledAppointmentId, initialAction]);

  // Fetch specialities when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSpecialities();
    }
  }, [isOpen]);

  // Fetch hospitals when UHID is entered/changed (for city-based filtering)
  useEffect(() => {
    if (isOpen && formData.uhid && formData.uhid.length >= 2) {
      fetchHospitals(formData.uhid);
    } else if (isOpen && !formData.uhid) {
      // Clear hospitals if no UHID
      setHospitals([]);
    }
  }, [isOpen, formData.uhid]);

  // Fetch doctors when hospital AND speciality are selected
  useEffect(() => {
    if (formData.hospital && formData.speciality) {
      fetchDoctors(formData.hospital, formData.speciality);
    }
  }, [formData.hospital, formData.speciality]);

  // Fetch available slots when doctor and date are selected
  useEffect(() => {
    if (formData.doctor && formData.appointmentDate) {
      fetchDoctorSlots(formData.doctor, formData.appointmentDate);
    }
  }, [formData.doctor, formData.appointmentDate]);

  // Fetch doctors when newHospital AND newSpeciality are selected (for rescheduling)
  useEffect(() => {
    if (formData.newHospital && formData.newSpeciality) {
      fetchDoctors(formData.newHospital, formData.newSpeciality);
    }
  }, [formData.newHospital, formData.newSpeciality]);

  // Fetch available slots for rescheduling when doctor and date are selected
  useEffect(() => {
    if (formData.newDoctor && formData.newDate && formData.rescheduleId) {
      fetchRescheduleSlots(formData.newDoctor, formData.newDate, formData.rescheduleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.newDoctor, formData.newDate]);

  // Fetch specialities filtered by hospital when hospital is selected for new appointment
  useEffect(() => {
    if (formData.hospital) {
      fetchSpecialities(formData.hospital);
    }
  }, [formData.hospital]);

  // Fetch specialities filtered by hospital when hospital is selected for reschedule
  useEffect(() => {
    if (formData.newHospital) {
      fetchSpecialities(formData.newHospital);
    }
  }, [formData.newHospital]);

  const fetchHospitals = async (uhid = null) => {
    try {
      let url = '/backend/api/hospitals';
      if (uhid) {
        url += `?uhid=${encodeURIComponent(uhid)}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.success) {
        setHospitals(data.hospitals);
        if (data.cityFilter) {
          console.log(`Hospitals filtered by city: ${data.cityFilter}`);
        }
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };

  const fetchSpecialities = async (hospital = null) => {
    try {
      let url = '/backend/api/specialities';
      if (hospital) {
        url += `?hospital=${encodeURIComponent(hospital)}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.success) {
        setSpecialities(data.specialities);
      }
    } catch (error) {
      console.error('Error fetching specialities:', error);
    }
  };

  const fetchDoctors = async (hospital, speciality) => {
    try {
      setLoading(true);
      let url = `/backend/api/doctors?hospital=${encodeURIComponent(hospital)}`;
      if (speciality) {
        url += `&speciality=${encodeURIComponent(speciality)}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.success) {
        setDoctors(data.doctors);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorSlots = async (doctorName, date = null) => {
    try {
      setLoading(true);
      let url = `/backend/api/doctors/${encodeURIComponent(doctorName)}/slots`;
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.success) {
        setAvailableSlots(data.slots);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRescheduleSlots = async (doctorName, date, appointmentId) => {
    try {
      setLoading(true);
      let url = `/backend/api/doctors/${encodeURIComponent(doctorName)}/available-slots`;
      url += `?date=${encodeURIComponent(date)}&appointment_id=${encodeURIComponent(appointmentId)}`;
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.success) {
        setAvailableSlots(data.slots);
      }
    } catch (error) {
      console.error('Error fetching reschedule slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset dependent fields
    if (name === 'uhid') {
      // Reset hospital and all dependent fields when UHID changes
      setFormData(prev => ({ ...prev, hospital: '', speciality: '', doctor: '', slot: '' }));
      setDoctors([]);
      setAvailableSlots([]);
    }
    if (name === 'hospital' || name === 'newHospital') {
      setFormData(prev => ({ ...prev, speciality: '', doctor: '', slot: '', newSpeciality: '', newDoctor: '', newSlot: '' }));
      setDoctors([]);
      setAvailableSlots([]);
    }
    if (name === 'speciality' || name === 'newSpeciality') {
      setFormData(prev => ({ ...prev, doctor: '', slot: '', newDoctor: '', newSlot: '' }));
      setDoctors([]);
      setAvailableSlots([]);
    }
    if (name === 'doctor' || name === 'newDoctor') {
      setFormData(prev => ({ ...prev, slot: '', newSlot: '' }));
      setAvailableSlots([]);
    }
  };

  const handleBack = () => {
    setSelectedOption(null);
    setFormData({
      uhid: '',
      hospital: '',
      speciality: '',
      doctor: '',
      appointmentDate: '',
      slot: '',
      rescheduleId: '',
      newHospital: '',
      newSpeciality: '',
      newDoctor: '',
      newDate: '',
      newSlot: '',
      cancelId: '',
      cancelReason: '',
      cancelComments: ''
    });
    setDoctors([]);
    setAvailableSlots([]);
  };

  const handleNewAppointment = async (e) => {
    e.preventDefault();

    const appointmentData = {
      uhid: formData.uhid,
      hospital: formData.hospital,
      doctor: formData.doctor,
      date: formData.appointmentDate,
      slot: formData.slot
    };

    console.log('New Appointment:', appointmentData);

    try {
      setLoading(true);
      const response = await apiFetch('/backend/api/appointments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(appointmentData)
      });
      
      if (response.status === 401) {
        alert('Your session has expired. Please log in again to book an appointment.');
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        alert(`Appointment booked successfully!\nAppointment ID: ${result.appointment_id}\nDoctor: ${formData.doctor}\nHospital: ${formData.hospital}\nDate: ${formData.appointmentDate}\nSlot: ${formData.slot}`);

        // Call onSuccess callback to refresh UHID list and notify chat
        if (onSuccess) {
          console.log('✅ Calling onSuccess callback with appointment ID:', result.appointment_id);
          onSuccess(result.appointment_id);
        }

        handleBack();
        onClose();
      } else {
        alert(result.error || 'Error booking appointment');
      }
    } catch (error) {
      alert('Error booking appointment. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();

    const rescheduleData = {
      hospital: formData.newHospital,
      doctor: formData.newDoctor,
      date: formData.newDate,
      slot: formData.newSlot
    };

    console.log('Reschedule:', rescheduleData);

    try {
      setLoading(true);
      const response = await apiFetch(`/backend/api/appointments/${formData.rescheduleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rescheduleData)
      });
      
      if (response.status === 401) {
        alert('Your session has expired. Please log in again to reschedule.');
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        alert('Appointment rescheduled successfully!');

        // Call onSuccess callback to refresh UHID list
        if (onSuccess) {
          console.log('✅ Calling onSuccess callback after appointment reschedule...');
          onSuccess();
        }

        handleBack();
        onClose();
      } else {
        alert(result.error || 'Error rescheduling appointment');
      }
    } catch (error) {
      alert('Error rescheduling appointment. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (e) => {
    e.preventDefault();

    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiFetch(`/backend/api/appointments/${formData.cancelId}`, {
        method: 'DELETE'
      });
      
      if (response.status === 401) {
        alert('Your session has expired. Please log in again to cancel appointments.');
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        alert('Appointment cancelled successfully!');

        // Call onSuccess callback to refresh UHID list
        if (onSuccess) {
          console.log('✅ Calling onSuccess callback after appointment cancellation...');
          onSuccess();
        }

        handleBack();
        onClose();
      } else {
        alert(result.error || 'Error cancelling appointment');
      }
    } catch (error) {
      alert('Error cancelling appointment. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {!selectedOption ? 'Appointment Options' : 
               selectedOption === 'new' ? 'New Appointment' :
               selectedOption === 'reschedule' ? 'Reschedule Appointment' :
               'Cancel Appointment'}
            </h2>
            <button
              onClick={() => {
                handleBack();
                onClose();
              }}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Back Button */}
          {selectedOption && (
            <button
              onClick={() => setSelectedOption(null)}
              className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Options
            </button>
          )}

          {!selectedOption ? (
            // Options View - Card Based
            <div className="space-y-4">
              {/* New Appointment Card */}
              <button
                onClick={() => setSelectedOption('new')}
                className="w-full bg-white border-2 border-gray-200 hover:border-blue-500 rounded-xl p-4 flex items-start gap-4 transition-all hover:shadow-lg group text-left"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500 transition-colors">
                  <Calendar className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">New Appointment</h3>
                  <p className="text-sm text-gray-600">Book a new appointment with a doctor</p>
                </div>
              </button>

              {/* Reschedule Appointment Card */}
              <button
                onClick={() => setSelectedOption('reschedule')}
                className="w-full bg-white border-2 border-gray-200 hover:border-orange-500 rounded-xl p-4 flex items-start gap-4 transition-all hover:shadow-lg group text-left"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500 transition-colors">
                  <RefreshCw className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Reschedule Appointment</h3>
                  <p className="text-sm text-gray-600">Change your existing appointment time</p>
                </div>
              </button>

              {/* Cancel Appointment Card */}
              <button
                onClick={() => setSelectedOption('cancel')}
                className="w-full bg-white border-2 border-gray-200 hover:border-red-500 rounded-xl p-4 flex items-start gap-4 transition-all hover:shadow-lg group text-left"
              >
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-500 transition-colors">
                  <XCircle className="w-6 h-6 text-red-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Cancel Appointment</h3>
                  <p className="text-sm text-gray-600">Cancel your scheduled appointment</p>
                </div>
              </button>
            </div>
          ) : selectedOption && (
            <>
              {/* Selected UHID Banner */}
              {prefilledUHID && selectedOption === 'new' && (
                <div className="mb-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-green-600 font-semibold uppercase tracking-wider">Auto-Selected UHID</p>
                      <p className="text-lg font-mono font-bold text-green-700">{prefilledUHID}</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  </div>
                </div>
              )}

              {/* Selected Appointment ID Banner */}
              {prefilledAppointmentId && (selectedOption === 'reschedule' || selectedOption === 'cancel') && (
                <div className={`mb-4 bg-gradient-to-r ${
                  selectedOption === 'reschedule'
                    ? 'from-orange-50 to-orange-100 border-orange-300'
                    : 'from-red-50 to-red-100 border-red-300'
                } border-2 rounded-lg p-4`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${
                      selectedOption === 'reschedule'
                        ? 'from-orange-400 to-orange-600'
                        : 'from-red-400 to-red-600'
                    } rounded-full flex items-center justify-center shadow-md flex-shrink-0`}>
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs ${
                        selectedOption === 'reschedule' ? 'text-orange-600' : 'text-red-600'
                      } font-semibold uppercase tracking-wider`}>Auto-Selected Appointment ID</p>
                      <p className={`text-lg font-mono font-bold ${
                        selectedOption === 'reschedule' ? 'text-orange-700' : 'text-red-700'
                      }`}>{prefilledAppointmentId}</p>
                    </div>
                    <CheckCircle className={`w-6 h-6 ${
                      selectedOption === 'reschedule' ? 'text-orange-600' : 'text-red-600'
                    } flex-shrink-0`} />
                  </div>
                </div>
              )}

              {/* Forms */}
              {selectedOption === 'new' && (
                <form onSubmit={handleNewAppointment} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patient UHID {prefilledUHID && <span className="text-green-600 text-xs">(Auto-filled - editable)</span>}
                    </label>
                    <input
                      type="text"
                      name="uhid"
                      value={formData.uhid}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        prefilledUHID
                          ? 'border-green-400 bg-green-50 text-green-800 font-semibold focus:ring-green-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Enter Patient UHID or select from Find My UHID"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Hospital {formData.uhid && hospitals.length > 0 && <span className="text-green-600 text-xs">(Filtered by your city)</span>}
                    </label>
                    <select
                      name="hospital"
                      value={formData.hospital}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.uhid}
                    >
                      <option value="">
                        {!formData.uhid ? 'Enter UHID First' :
                         hospitals.length === 0 ? 'No hospitals in your city' :
                         'Choose Hospital'}
                      </option>
                      {hospitals.map(hospital => (
                        <option key={hospital} value={hospital}>{hospital}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Speciality
                    </label>
                    <select
                      name="speciality"
                      value={formData.speciality}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.hospital}
                    >
                      <option value="">
                        {!formData.hospital ? 'Select Hospital First' : 'Choose Speciality'}
                      </option>
                      {specialities.map(speciality => (
                        <option key={speciality} value={speciality}>{speciality}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Doctor
                    </label>
                    <select
                      name="doctor"
                      value={formData.doctor}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.hospital || !formData.speciality || loading}
                    >
                      <option value="">
                        {!formData.hospital ? 'Select Hospital First' :
                         !formData.speciality ? 'Select Speciality First' :
                         loading ? 'Loading doctors...' :
                         doctors.length === 0 ? 'No doctors available for selected speciality' :
                         'Choose Doctor'}
                      </option>
                      {doctors.map(doc => (
                        <option key={doc.name} value={doc.name}>
                          {doc.name} ({doc.experience})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment Date
                    </label>
                    <input
                      type="date"
                      name="appointmentDate"
                      value={formData.appointmentDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Time Slots
                    </label>
                    <select
                      name="slot"
                      value={formData.slot}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.doctor || loading}
                    >
                      <option value="">
                        {!formData.doctor ? 'Select Doctor First' : 
                         loading ? 'Loading slots...' : 
                         availableSlots.length === 0 ? 'No slots available' :
                         'Choose Time Slot'}
                      </option>
                      {availableSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2 rounded-lg transition-colors ${
                      loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {loading ? 'Booking...' : 'Book Appointment'}
                  </button>
                </form>
              )}

              {selectedOption === 'reschedule' && (
                <form onSubmit={handleReschedule} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment ID {prefilledAppointmentId && initialAction === 'reschedule' && <span className="text-orange-600 text-xs">(Auto-filled - editable)</span>}
                    </label>
                    <input
                      type="text"
                      name="rescheduleId"
                      value={formData.rescheduleId}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        prefilledAppointmentId && initialAction === 'reschedule'
                          ? 'border-orange-400 bg-orange-50 text-orange-800 font-semibold focus:ring-orange-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Enter Appointment ID or select from My Appointments"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Hospital
                    </label>
                    <select
                      name="newHospital"
                      value={formData.newHospital}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Choose Hospital</option>
                      {hospitals.map(hospital => (
                        <option key={hospital} value={hospital}>{hospital}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Speciality
                    </label>
                    <select
                      name="newSpeciality"
                      value={formData.newSpeciality}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.newHospital}
                    >
                      <option value="">
                        {!formData.newHospital ? 'Select Hospital First' : 'Choose Speciality'}
                      </option>
                      {specialities.map(speciality => (
                        <option key={speciality} value={speciality}>{speciality}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Doctor
                    </label>
                    <select
                      name="newDoctor"
                      value={formData.newDoctor}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Fetch slots for new doctor if date is selected
                        if (e.target.value && formData.newDate && formData.rescheduleId) {
                          fetchRescheduleSlots(e.target.value, formData.newDate, formData.rescheduleId);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.newHospital || !formData.newSpeciality || loading}
                    >
                      <option value="">
                        {!formData.newHospital ? 'Select Hospital First' :
                         !formData.newSpeciality ? 'Select Speciality First' :
                         loading ? 'Loading doctors...' :
                         doctors.length === 0 ? 'No doctors available for selected speciality' :
                         'Choose Doctor'}
                      </option>
                      {doctors.map(doc => (
                        <option key={doc.name} value={doc.name}>
                          {doc.name} ({doc.experience})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Date
                    </label>
                    <input
                      type="date"
                      name="newDate"
                      value={formData.newDate}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Fetch slots for selected date if doctor is selected
                        if (formData.newDoctor && e.target.value && formData.rescheduleId) {
                          fetchRescheduleSlots(formData.newDoctor, e.target.value, formData.rescheduleId);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Time Slot
                    </label>
                    <select
                      name="newSlot"
                      value={formData.newSlot}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!formData.newDoctor || loading}
                    >
                      <option value="">
                        {!formData.newDoctor ? 'Select Doctor First' : 
                         loading ? 'Loading slots...' : 
                         availableSlots.length === 0 ? 'No slots available' :
                         'Choose Time Slot'}
                      </option>
                      {availableSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2 rounded-lg transition-colors ${
                      loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {loading ? 'Rescheduling...' : 'Reschedule Appointment'}
                  </button>
                </form>
              )}

              {selectedOption === 'cancel' && (
                <form onSubmit={handleCancel} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment ID {prefilledAppointmentId && initialAction === 'cancel' && <span className="text-red-600 text-xs">(Auto-filled - editable)</span>}
                    </label>
                    <input
                      type="text"
                      name="cancelId"
                      value={formData.cancelId}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        prefilledAppointmentId && initialAction === 'cancel'
                          ? 'border-red-400 bg-red-50 text-red-800 font-semibold focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Enter Appointment ID or select from My Appointments"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Cancellation
                    </label>
                    <select
                      name="cancelReason"
                      value={formData.cancelReason}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Reason</option>
                      <option value="schedule-conflict">Schedule Conflict</option>
                      <option value="feeling-better">Feeling Better</option>
                      <option value="emergency">Emergency</option>
                      <option value="doctor-unavailable">Doctor Unavailable</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Comments
                    </label>
                    <textarea
                      name="cancelComments"
                      value={formData.cancelComments}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      placeholder="Optional"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Cancel Appointment
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;